import classNames from 'classnames';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PasswordInputField } from '@/components/InputFields';
import useFocusFirstInvalidField from '@/hooks/use-focus-first-invalid-field';
import Button from '@/shared/components/Button';
import ErrorMessage from '@/shared/components/ErrorMessage';
import StrengthMeter from '@/shared/components/InputFields/PasswordInputField/StrengthMeter';

import HiddenIdentifierInput from './HiddenIdentifierInput';

type Props = {
  readonly className?: string;
  // eslint-disable-next-line react/boolean-prop-naming
  readonly autoFocus?: boolean;
  readonly onSubmit: (password: string) => Promise<void>;
  readonly errorMessage?: string;
  readonly clearErrorMessage?: () => void;
  /** Whether the password is being set in a reset password flow. */
  readonly isForgotPassword?: boolean;
};

type FieldState = {
  newPassword: string;
};

const Lite = ({
  className,
  autoFocus,
  onSubmit,
  errorMessage,
  clearErrorMessage,
  isForgotPassword,
}: Props) => {
  const { t } = useTranslation();

  const {
    register,
    watch,
    clearErrors,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FieldState>({
    reValidateMode: 'onBlur',
    defaultValues: { newPassword: '' },
  });

  const newPasswordValue = watch('newPassword');

  const formRef = useFocusFirstInvalidField(isSubmitting);

  useEffect(() => {
    if (!isValid) {
      clearErrorMessage?.();
    }
  }, [clearErrorMessage, isValid]);

  const onSubmitHandler = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      clearErrorMessage?.();

      await handleSubmit(async (data) => {
        await onSubmit(data.newPassword);
      })(event);
    },
    [clearErrorMessage, handleSubmit, onSubmit]
  );

  return (
    <form
      ref={formRef}
      className={classNames('flex w-full flex-col gap-4', className)}
      onSubmit={onSubmitHandler}
    >
      <HiddenIdentifierInput isForgotPassword={isForgotPassword} />
      <div className="flex flex-col gap-1">
        <PasswordInputField
          autoComplete="new-password"
          label={t('input.password')}
          autoFocus={autoFocus}
          enterKeyHint="done"
          // A server rejection (policy, breached list, same as old) is about this field.
          isDanger={!!errors.newPassword || !!errorMessage}
          errorMessage={errors.newPassword?.message}
          {...register('newPassword', {
            required: t('error.password_required'),
            onChange: () => {
              if (errors.newPassword) {
                clearErrors('newPassword');
              }
            },
          })}
        />
        <StrengthMeter password={newPasswordValue} />
      </div>

      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

      <Button
        name="submit"
        title="action.save_password"
        htmlType="submit"
        isLoading={isSubmitting}
        className="mt-2"
      />

      <input hidden type="submit" />
    </form>
  );
};

export default Lite;
